import React, { useEffect, useState, useMemo } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer, shuffleQuizOptions } from "../../utils/helpers";
import { updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import { modalConfirm, modalQuestionResolution, modalHint, modalHintShow } from "../../utils/logger";
import { buildQuizFeedbackBlockHtml, buildQuizResultAvatarHtml } from "../../utils/quizFeedbackHtml";
import { IconHandClick } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import estasseguro from "../../assets/bg/estasseguro-avatar-green.png";
import respuestacorrecta from "../../assets/bg/correcto-avatar-pre.png";
import respuestaincorrecta from "../../assets/bg/incorrecto-avatar-pre.png";
import btnPistaGreen from "../../assets/btnPistaGreen.png";
import imageHint from "../../assets/hints/imageHintGreen.png";
import imageHintUsed from "../../assets/btnPistaGreen.png";
import unIntento from "../../assets/un-intento-green.png";
import dosIntentos from "../../assets/dos-intentos-green.png";

const GREEN = "#53CE76";

const SCENARIO5_QUIZ1_BASE_OPTIONS = [
	{
		id: 1,
		text: "Mantener ADT e iniciar docetaxel.",
		correct: true,
		explanation: "",
	},
	{
		id: 2,
		text: "Sustituir apalutamida por enzalutamida.",
		correct: false,
		explanation: "",
	},
	{
		id: 3,
		text: "Iniciar cabazitaxel.",
		correct: false,
		explanation: "",
	},
	{
		id: 4,
		text: "Mantener apalutamida y esperar a nueva progresión.",
		correct: false,
		explanation: "",
	},
];

function Scenario5Quiz1() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);

	const activeScenario = getScenarioNumByPath(location.pathname);

	const QUIZ_ID = QUIZZES.QUIZ14;
	const SCENARIO_ID = SCENARIOS.SCENARIO_5;

	const [selectedOption, setSelectedOption] = useState(null);
	const [isCompleted, setIsCompleted] = useState(false);
	const [hintUsed, setHintUsed] = useState(false);
	const [hintPenalty, setHintPenalty] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [attempt, setAttempt] = useState(1);
	const [blockedOptions, setBlockedOptions] = useState(new Set());
	const [pendingScore, setPendingScore] = useState(() => Number(group?.score || 0));
	const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
	const [seededBaseScore, setSeededBaseScore] = useState(false);

	useEffect(() => {
		if (seededBaseScore) return;
		if (!group) return;
		const s = Number(group?.score);
		if (!Number.isFinite(s)) return;
		setPendingScore(s);
		setSeededBaseScore(true);
	}, [group, seededBaseScore]);

	const NUMBERATTEMPTS = 2;
	const CORRECT_ANSWER_POINTS = 10;
	const WRONG_ANSWER_POINTS = -5;
	const CORRECT_ANSWER_POINTS_2 = 3;
	const WRONG_ANSWER_POINTS_2 = 0;

	const retroalimentacion =
		"En un paciente que progresa con ADT + apalutamida, la estrategia más sólida, si mantiene buen estado general y es candidato a tratamiento citotóxico, es continuar la castración e iniciar docetaxel. Las guías EAU recomiendan quimioterapia tras progresión a un ARPI y desaconsejan la secuenciación sistemática entre agentes dirigidos al receptor androgénico, por la limitada eficacia cruzada. Las decisiones posteriores deben individualizarse según tratamientos previos, síntomas, comorbilidades, perfil genómico y patrón metastásico.";
	const retroalimentacionBlockHtml = buildQuizFeedbackBlockHtml(retroalimentacion, "green");

	const displayOptions = useMemo(() => shuffleQuizOptions(SCENARIO5_QUIZ1_BASE_OPTIONS), []);

	async function submitAnswer(finalScore, correctFinal) {
		const totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));

		try {
			const res = await postAnswer({
				scenario: SCENARIO_ID,
				quiz: QUIZ_ID,
				group: group?._id,
				score: finalScore,
				time: totalTime,
				lastVisited: ROUTES.SCENARIO5_FIN,
				correct: correctFinal,
			});

			if (res.status === 201) {
				const serverGroup = res.data?.group;
				if (serverGroup) {
					dispatch({ type: "GROUP_SET", payload: serverGroup });
				}
				setIsCompleted(true);
				navigate(ROUTES.SCENARIO5_FIN);
			}
		} catch (error) {
			const status = error?.response?.status;
			const code = error?.response?.data?.code;

			if (status === 409 && code === "E_DUPLICATE_ANSWER") {
				await modalQuestionResolution({
					correct: false,
					question: "Respuesta ya registrada",
					textHtml: `<div class="txt32 bold" style="color:${GREEN}">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, pueden continuar con el resto del juego.</div>`,
					customConfirmButtonHtml: "Continuar",
					color: "green",
				});
				navigate(ROUTES.SCENARIO5_FIN);
				return;
			}

			await modalQuestionResolution({
				correct: false,
				question: "No se pudo guardar",
				textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
				customConfirmButtonHtml: "Aceptar",
				color: "green",
			});
			setIsSubmitting(false);
		}
	}

	const handleHint = async () => {
		const hint =
			"Tras progresión a un ARPI en enfermedad metastásica, piensa en el valor de la quimioterapia y evita la simple secuenciación entre hormonales de mecanismo próximo.";
		if (hintUsed) {
			await modalHintShow({ hint, imageHintUsed, color: "green", big: false });
			return;
		}
		const { used } = await modalHint({ image: imageHint, imageHintUsed, hint, color: "green", big: false });
		if (used && !hintUsed) {
			setHintUsed(true);
			setHintPenalty(3);
		}
	};

	function checkAnswer() {
		if (!selectedOption) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<img src=${estasseguro} alt="¿Estás seguro?" class="estasseguro" /><div class="text-center txtTitulos mt-3 green">Deben seleccionar una opción</div>`,
				color: "green",
			});
			return;
		}

		const option = displayOptions.find((o) => o.id === selectedOption);
		if (!option) return;

		if (blockedOptions.has(option.id)) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<div class="txt24 txt-red bold">Esa opción ya la intentaste y era incorrecta. Elijan otra.</div>`,
				customConfirmButtonHtml: "Aceptar",
				color: "green",
			});
			return;
		}

		modalConfirm({ estasseguro, color: "green" }).then((res) => {
			if (!res.isConfirmed) return;
			setIsSubmitting(true);

			const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;

			if (option.correct) {
				const base = attempt === 1 ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
				const attemptDelta = base - hintThisAttempt;
				const newScore = pendingScore + attemptDelta;
				if (hintThisAttempt) setHintAppliedToScore(true);

				modalQuestionResolution({
					correct: true,
					question: option.text,
					textHtml: `${buildQuizResultAvatarHtml(respuestacorrecta, "correct", "green")}
                     <div class="txt26 bold pt-4">Ganaron ${attemptDelta} puntos en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                     ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                     <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>
                     ${retroalimentacionBlockHtml}`,
					customConfirmButtonHtml: "Continuar",
					color: "green",
				}).then(() => {
					submitAnswer(newScore, true);
				});

				return;
			}

			if (attempt < NUMBERATTEMPTS) {
				const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt;
				const newScore = pendingScore + attemptDelta;
				if (hintThisAttempt) setHintAppliedToScore(true);

				modalQuestionResolution({
					correct: false,
					question: option.text,
					textHtml: `${buildQuizResultAvatarHtml(respuestaincorrecta, "incorrect", "green")}
                     <div class="txt28 bold pt-3">Respuesta incorrecta ${attemptDelta} puntos${hintThisAttempt ? " (incluye -3 por pista)" : ""}</div>
                     <div class="txt22 mt-2">Les queda <b>1</b> intento. Elijan otra opción.</div>
                     <div class="txt20 mt-2">Acumulado hasta ahora: <b>${newScore}</b></div>`,
					customConfirmButtonHtml: "Intentar de nuevo",
					color: "green",
				}).then(() => {
					setPendingScore(newScore);
					setBlockedOptions((prev) => new Set(prev).add(option.id));
					setAttempt(attempt + 1);
					setSelectedOption(null);
					setIsSubmitting(false);
				});

				return;
			}

			const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt;
			const newScore = pendingScore + attemptDelta;
			if (hintThisAttempt) setHintAppliedToScore(true);

			modalQuestionResolution({
				correct: false,
				question: option.text,
				textHtml: `${buildQuizResultAvatarHtml(respuestaincorrecta, "incorrect", "green")}
                   <div class="txt26 bold pt-4">Han agotado los intentos</div>
                   ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3 por pista en este intento</div>` : ""}
                   <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>
                   ${retroalimentacionBlockHtml}`,
				customConfirmButtonHtml: "Continuar",
				color: "green",
			}).then(() => {
				submitAnswer(newScore, false);
			});
		});
	}

	const remainingAttempts = NUMBERATTEMPTS - attempt + 1;

	return (
		<div className="h-100 d-flex flex-column">
			<div className="container-fluid px-5 pt-3 pb-5 mb-5 flex-grow-1 bgCircleGreen2 overflow-auto">
				<h3
					className="txt22 txt-white text-center mb-4"
					style={{ backgroundColor: GREEN, borderRadius: "10px", padding: "18px 20px", lineHeight: 1.45 }}
				>
					Si el paciente presenta progresión bioquímica y radiológica RECIST durante el tratamiento con ADT +
					apalutamida, ¿qué estrategia terapéutica subsecuente sería la más adecuada?
				</h3>

				<div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
					<h5 className="mb-0 txt20 text-left gray d-flex align-items-center gap-2">
						<IconHandClick size={28} color="#757575" stroke={1} /> Seleccionen la respuesta correcta
					</h5>
					<div>
						<img src={remainingAttempts === 2 ? dosIntentos : unIntento} className="imgIntentos" alt="" />
					</div>
				</div>

				<div className="row g-4 mb-2">
					{displayOptions.map((option) => {
						const isSelected = selectedOption === option.id;
						const isBlocked = blockedOptions.has(option.id);
						return (
							<div key={option.id} className="col-md-6">
								<div
									className={`cursor-pointer position-relative p-3 rounded shadow h-100 d-flex justify-content-center align-items-center ${isSelected ? "selected" : ""} ${isBlocked ? "opacity-50" : ""}`}
									style={{
										minHeight: "100px",
										border: isSelected ? `1px solid ${GREEN}99` : "1px solid #fff",
										backgroundColor: isSelected ? `${GREEN}44` : "#fff",
										pointerEvents: isBlocked ? "none" : "auto",
									}}
									onClick={() => !isBlocked && setSelectedOption(option.id)}
									aria-disabled={isBlocked}
									title={isBlocked ? "Opción bloqueada (ya fue incorrecta)" : "Seleccionar"}
								>
									<div className="txtTexto1 text-center gray">{option.text}</div>
									{isBlocked && (
										<span className="badge bg-secondary position-absolute top-0 end-0 m-1">Bloqueada</span>
									)}
								</div>
							</div>
						);
					})}
				</div>

				<div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-4 pt-2">
					<div className="position-relative d-inline-block">
						<img
							src={btnPistaGreen}
							className={`btnPista ${hintUsed ? "used" : ""}`}
							onClick={handleHint}
							onKeyDown={(e) => {
								if (e.key === "Enter") void handleHint();
							}}
							alt={hintUsed ? "Pista usada" : "Usar pista"}
							title={hintUsed ? "Pista usada (-3)" : "Usar pista (-3 puntos)"}
							role="button"
							tabIndex={0}
						/>
						{hintUsed && <span className="badge-penalty">-3</span>}
					</div>
					<CheckButton
						text="Comprobar respuesta"
						onClick={checkAnswer}
						disabled={isCompleted || isSubmitting}
						color="green"
						loading={isSubmitting}
					/>
				</div>
			</div>

			<ScenarioBar activeScenario={activeScenario} />
		</div>
	);
}

export default injectIntl(Scenario5Quiz1);
